import {
  ActionMenu,
  ActionMenuItem,
  ActionMenuText,
  Box,
  DataTable,
  FAIcon,
  Flex,
  InputField,
  useApi,
  useDebounce,
  SecondaryOutlinedButton,
} from '@fivehealth/botero'
import { faSearch, faTimes, faCog } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty, chain } from 'lodash'
import qs from 'qs'
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import Filter from 'old-code/views/Filter/Filter'
import qsDecoder from 'old-code/views/Filter/qsDecoder'
import DeleteConfirmation from 'old-code/components/DeleteConfirmation/DeleteConfirmation'
import FavouritesForm from 'old-code/views/Favourites/FavouritesForm'
import ProgressAnimation from 'old-code/views/BotAnalytics/components/ProgressAnimation'
import { constructACL } from 'old-code/views/Favourites/Utils'
import DropdownMenu from 'old-code/components/DropdownMenu/DropdownMenu'
import SpinningIcon from 'old-code/components/Loaders/SpinningIcon'
import useCompleteDepartments from 'old-code/customHooks/useCompleteDepartments'
import useCompleteDesignations from 'old-code/customHooks/useCompleteDesignations'
import { useEinsteinAdministratorProfile } from 'old-code/context/EinsteinAdministratorContext'
import { useModal } from '../../../context/ModalContext'
import {
  onToggleFilterCallback,
  tableConfigUtils,
  getMimeType,
  extractUniqueKeyFromAclDnf,
  removeEmptyKeys,
} from '../../../../Utils'
import EditDocumentModal from './DocumentEditModal'
import FileCell from './FileCell'
import EventsConstant from '../../../config/constants/events.constants'

const getDisabledActions = (einsteinAdmin, cell) =>
  !einsteinAdmin?.isSuper && // not a super admin
  !einsteinAdmin?.divisions.length && // not a division admin
  einsteinAdmin?.departments.length && // is a department admin
  cell?.row?.original?.misc?.ownerDivisions?.edges?.length // document by division admin

const ActionsCell = (props) => {
  const {
    cell,
    onShowEditDocModal,
    onAddToFavorites,
    history,
    parentPage,
    einsteinAdmin,
    postHogModule,
    logPostHogEvent,
  } = props
  const disableActions = getDisabledActions(einsteinAdmin, cell)
  return (
    <Flex justifyContent="end" alignItems="center">
      <Box mr={3}>
        <FileCell {...props} />
      </Box>
      <DocumentsActionMenu
        postHogModule={postHogModule}
        logPostHogEvent={logPostHogEvent}
        disableActions={disableActions}
        onShowEditDocModal={(path) =>
          onShowEditDocModal(get(cell, 'row.original', {}), path)
        }
        onAddToFavorites={() => onAddToFavorites(get(cell, 'row.original', {}))}
        history={history}
        parentPage={parentPage}
      />
    </Flex>
  )
}

const DocumentsTable = ({
  RowSelectComponent,
  enableRowEdit = true,
  enableRowSelect = true,
  tableSettings,
  hospital,
  mimeType = { options: [] },
  setRowEditState,
  currentFilters,
  onSort,
  onSetTotalCount,
  parentPage,
  selectedTabId,
  logPostHogEvent,
  postHogModule,
  bulkActionsPerformed,
}) => {
  const queryClient = useQueryClient()
  const [rowSelectionType, setRowSelectionType] = useState('') // customSelect, selectAllVisible, selectAll

  const defaultParams = qs.parse(window.location.search.substr(1), {
    decoder: qsDecoder,
  })
  // EVENTTRACKER: External files event not logging
  const tableParams = useRef(defaultParams)

  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const { openModal, closeModal } = useModal()

  const debouncedSearchQuery = useDebounce(searchQuery, 600)
  useEffect(() => {
    logPostHogEvent(`${postHogModule}:search_submit`, {
      search_term: debouncedSearchQuery,
    })
  }, [debouncedSearchQuery])
  const departmentValueLabelPairs = useCompleteDepartments({
    viewAll: true,
    uidInValue: true,
  })
  const designationValueLabelPairs = useCompleteDesignations()

  const [showFilters, setShowFilters] = useState(false)
  const [activeFilters, setActiveFilters] = useState(defaultParams)
  const [filter, setFilter] = useState({
    searchQuery: '',
  })
  const [internalDocs, setInternalDocs] = useState([])

  function getKeyByValue(object, value) {
    return Object.keys(object).find((key) => object[key] === value)
  }

  function getKeysByValue(object, value) {
    return Object.keys(object).filter((key) => object[key] === value)
  }

  useEffect(() => {
    queryClient.invalidateQueries('einsteinDocuments')
  }, [])

  useEffect(() => {
    if (bulkActionsPerformed) {
      setRowSelectionType('')
    }
  }, [setRowSelectionType, bulkActionsPerformed])

  const {
    queries: {
      useEinsteinAdministrator,
      useEinsteinDocumentEntrys,
      useEinsteinDocumentEntryUpdate,
      useDivisions,
    },
  } = useApi({
    queries: [
      'useEinsteinAdministrator',
      'useEinsteinDocumentEntrys',
      'useEinsteinDocumentEntryUpdate',
      'useDivisions',
    ],
  })

  const { data: dataDivision } = useDivisions().paginated({
    variables: {},
  })
  const allDivisionOptions = useMemo(() => {
    const desingations = dataDivision?.pages?.[0] || []
    const list = desingations
    const uniqueUids = new Set(list?.map((obj) => obj.uid))

    const uniqueDesignations = list?.filter(
      (obj, index) => Array.from(uniqueUids).indexOf(obj.uid) === index
    )
    return uniqueDesignations?.map((designation) => ({
      label: designation.name,
      value: designation.uid,
      type: 'division',
    }))
  }, [dataDivision])
  const divisionOwnerList = allDivisionOptions?.map((ele) => ({
    id: ele.label,
    label: ele.label,
    value: ele.label,
  }))

  const { data: currentAdmin } = useEinsteinAdministrator()
  const orgKey = currentAdmin?.hospital?.organizationKey

  const {
    data: documentsData,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useEinsteinDocumentEntrys({
    enabled: !!currentAdmin,
    variables: {
      dataSourceIn: [
        `${
          ((currentAdmin || {}).hospital || {}).organizationKey || ''
        }-documents`,
      ],
      ...filter,
    },
  })

  const actions = [
    {
      id: 'selectAll',
      label: 'Select all documents',
      onClick: () => {
        setRowEditState(true)
        setRowSelectionType('selectAll')
        logPostHogEvent(`${postHogModule}:actions_dropdown_option_click`, {
          option: 'selectAll',
        })
      },
      logEventProps: {
        subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE_INTERNAL,
        eventName: EventsConstant.DOCUMENTS_SELECT_ALL,
        page: EventsConstant.DOCUMENTS_PAGE,
      },
    },
    {
      id: 'customSelections',
      divider: 'true',
      enable: rowSelectionType !== 'selectAll',
      label: 'Custom selection',
      onClick: () => {
        setRowEditState(true)
        setRowSelectionType('customSelect')
        logPostHogEvent(`${postHogModule}:actions_dropdown_option_click`, {
          option: 'customSelections',
        })
      },
      logEventProps: {
        subSource: EventsConstant.USER_GROUP_TABLE_SOURCE,
        eventName: EventsConstant.USER_GROUP_ACTIONS_SELECT_CUSTOM,
        page: EventsConstant.USER_GROUP_PAGE,
      },
    },
  ]

  const { mutateAsync: updateDocument } = useEinsteinDocumentEntryUpdate()

  useEffect(() => {
    const newInternalDocs = []

    onSetTotalCount((documentsData || {}).totalCount || null)
    chain(documentsData)
      .get('pages', [])
      .flatMap((page) => page || [])
      .filter((doc) => !doc.isDeactivated)
      .uniqBy('uid')
      .map((doc) => {
        let folder = doc.filename.substring(
          0,
          doc.filename.lastIndexOf('/') + 1
        )

        if (folder[0] === '/') {
          folder = folder.substring(1)
        }

        let owner = ''
        if (doc?.ownerDepartments?.edges?.length) {
          owner = doc?.ownerDepartments?.edges?.[0]?.node?.name
        } else if (doc?.ownerDivisions?.edges?.length) {
          owner = doc?.ownerDivisions?.edges?.[0]?.node?.name
        }
        const docObj = {
          uid: doc?.uid,
          id: doc?.uid,
          title: doc?.title,
          owner,
          department: doc?.department,
          type: getMimeType(doc?.mimeType),
          status: doc?.variables?.status?.toLowerCase().includes('ignore')
            ? 'Ignored'
            : doc?.syncStatus,
          year: doc?.variables?.year,
          ignore:
            doc?.variables?.status?.toLowerCase().includes('ignore') || false,
          tags: doc?.variables?.tags ? doc?.variables?.tags : '',
          aclDnf: doc?.aclDnf,
          misc: doc,
          department_access:
            doc?.aclDnf && !isEmpty(doc?.aclDnf)
              ? extractUniqueKeyFromAclDnf('department', doc?.aclDnf)
              : 'All Departments',
          designation_access:
            doc?.aclDnf && !isEmpty(doc?.aclDnf)
              ? extractUniqueKeyFromAclDnf('designation', doc?.aclDnf)
              : 'All Designations',
        }

        newInternalDocs.push(docObj)

        return docObj
      })
      .value()

    setInternalDocs(newInternalDocs)
  }, [documentsData])

  const onSetFilters = (newFilters) => {
    const validFilters = Object.keys(newFilters).reduce((acc, current) => {
      if (!isEmpty(newFilters[current])) {
        acc[current] = newFilters[current]
      }

      return acc
    }, {})

    setFilter(validFilters)
  }

  const onApplyFilters = (appliedFilters) => {
    tableParams.current = {
      ...tableParams.current,
      ...activeFilters,
    }
    let selectedMimeTypeKey = []
    let selectedStatusKey = ''
    let syncedOn = ''
    let selectedDepDivKey = []
    const ownerDivisionsUidIn = []
    const ownerDepartmentUidIn = []
    const filters = removeEmptyKeys(appliedFilters)

    if (filters.mimeType) {
      selectedMimeTypeKey = getKeysByValue(filters.mimeType, true)
    }
    if (filters.status) {
      selectedStatusKey = getKeyByValue(filters.status, true)
    }

    if (filters.divisionOwner) {
      selectedDepDivKey = getKeysByValue(filters.divisionOwner, true)
      allDivisionOptions?.forEach((ele) => {
        if (selectedDepDivKey.includes(ele.label)) {
          ownerDivisionsUidIn.push(ele.value)
        }
      })
    }

    if (filters.departmentOwner) {
      selectedDepDivKey = getKeysByValue(filters.departmentOwner, true)
      departmentValueLabelPairs?.forEach((ele) => {
        if (selectedDepDivKey.includes(ele.label)) {
          ownerDepartmentUidIn.push(ele.value)
        }
      })
    }

    let departmentFiltered = filters?.department
    let designationFiltered = filters?.designation
    if (
      filters?.department &&
      Object.values(filters?.department)?.filter((e) => e)?.length ===
        departmentValueLabelPairs?.length
    ) {
      departmentFiltered = null
    }
    if (
      filters?.designation &&
      Object.values(filters?.designation)?.filter((e) => e)?.length ===
        designationValueLabelPairs?.length
    ) {
      designationFiltered = null
    }

    if (filters?.syncedOn) {
      const selectedSyncKey = getKeyByValue(filters.syncedOn, true)
      const now = new Date()
      switch (selectedSyncKey) {
        case 'LAST_1_DAY':
          now.setDate(now.getDate() - 1) // Subtract 1 day
          syncedOn = now.toISOString()
          break

        case 'LAST_7_DAYS':
          now.setDate(now.getDate() - 7) // Subtract 7 days
          syncedOn = now.toISOString()
          break

        case 'LAST_14_DAYS':
          now.setDate(now.getDate() - 14) // Subtract 14 days
          syncedOn = now.toISOString()
          break

        default:
          syncedOn = '' // Or any default value you consider appropriate
          break
      }
    }

    setShowFilters(false)
    tableParams.current = {
      mimeType_In: selectedMimeTypeKey,
      syncStatus: selectedStatusKey,
      // departmentIn: departmentKey,
      syncedOn_Gte: syncedOn,
      ...tableParams.current,
      ...activeFilters,
    }
    if (ownerDivisionsUidIn) {
      tableParams.current.ownerDivisions_Uid_In = ownerDivisionsUidIn
    }
    if (ownerDepartmentUidIn) {
      tableParams.current.ownerDepartments_Uid_In = ownerDepartmentUidIn
    }

    const filterObj = {
      mimeType_In: selectedMimeTypeKey,
      syncStatus: selectedStatusKey,
      // departmentIn: departmentKey,
      syncedOn_Gte: syncedOn,
      aclDnfOr: constructACL(departmentFiltered, designationFiltered, orgKey),
    }
    if (ownerDivisionsUidIn) {
      filterObj.ownerDivisions_Uid_In = ownerDivisionsUidIn
    }
    if (ownerDepartmentUidIn) {
      filterObj.ownerDepartments_Uid_In = ownerDepartmentUidIn
    }

    if (isEmpty(selectedStatusKey) || selectedStatusKey === undefined) {
      delete tableParams.current.syncStatus
      delete filterObj.syncStatus
    }
    if (isEmpty(syncedOn)) {
      delete tableParams.current.syncedOn_Gte
      delete filterObj.syncedOn_Gte
    }

    onSetFilters(filterObj)
  }

  const onToggleFilter = ({ nextFilterState }) => {
    onToggleFilterCallback(
      nextFilterState,
      currentFilters,
      activeFilters,
      setActiveFilters
    )
  }

  const onShowEditDocModal = (document) => {
    setRowEditState(false)
    openModal(
      <EditDocumentModal document={document} closeModal={closeModal} />,
      {
        overflow: 'scroll',
      }
    )
  }

  const onDeleteDocument = (formData) => {
    openModal(
      <DeleteConfirmation
        closeModal={closeModal}
        label="document"
        module="Documents & Media"
        onDeleteConfirm={() => {
          updateDocument({
            input: {
              uid: formData.uid,
              deactivatedOn: new Date().toISOString(),
            },
          }).then(() => {
            queryClient.invalidateQueries('einsteinDocuments')
            queryClient.invalidateQueries('einsteinDocumentsTotalCount')
            closeModal()
          })
        }}
      />
    )
  }

  const onAddToFavorites = (document) => {
    openModal(
      <FavouritesForm
        openModal={openModal}
        closeModal={closeModal}
        document={document}
        fromFileManager
      />,
      {
        overflow: 'scroll',
      }
    )
  }

  const onToggleFilters = (open) => {
    setShowFilters(open)
    if (!open) {
      const mimeTypeOnCancel = {}
      filter?.mimeType_in?.forEach((ele) => {
        mimeTypeOnCancel[ele] = true
      })
      const department = {}
      extractUniqueKeyFromAclDnf('department', filter.aclDnfOr)
        ?.split(',')
        .forEach((ele) => {
          department[ele] = true
        })
      const designation = {}
      extractUniqueKeyFromAclDnf('designation', filter.aclDnfOr)
        ?.split(',')
        .forEach((ele) => {
          designation[ele] = true
        })
      setActiveFilters({
        mimeType: mimeTypeOnCancel,
        department,
        designation,
        status: { [filter.syncStatus]: true },
      })
    }
  }

  const onResetFilters = () => {
    tableParams.current = {
      selectedMimeTypeKey: [],
    }
    // setShowFilters(false)
    setActiveFilters({})

    const filterObj = {
      selectedMimeTypeKey: [],
    }
    onSetFilters(filterObj)
  }

  const onFilterCancel = () => {
    setActiveFilters({
      ...tableParams.current,
    })
  }

  const fileTypeFilters = {
    id: 'mimeType',
    title: 'File Types',
    multiSelect: true,
    data: mimeType.options,
  }

  const statusFilters = {
    id: 'status',
    title: 'Status',
    multiSelect: false,
    data: [
      { id: 'LATEST_AVAILABLE', label: 'Synced' },
      { id: 'OUTDATED', label: 'Error' },
      { id: 'NOT_AVAILABLE', label: 'Pending' },
      { id: 'IGNORE', label: 'Ignored' },
    ],
  }

  const departmentFilters = {
    id: 'department',
    title: 'Department Access',
    multiSelect: true,
    data: departmentValueLabelPairs,
  }

  const syncedOnFilters = {
    id: 'syncedOn',
    title: 'Synced Within',
    shouldSort: false,
    multiSelect: false,
    data: [
      { id: 'LAST_1_DAY', label: 'Last 1 day' },
      { id: 'LAST_7_DAYS', label: 'Last 7 days' },
      { id: 'LAST_14_DAYS', label: 'Last 14 days' },
    ],
  }
  const divisionOwnerFilters = {
    id: 'divisionOwner',
    title: 'Division Owner',
    multiSelect: true,
    data: divisionOwnerList,
  }

  const departmentOwnerFilters = {
    id: 'departmentOwner',
    title: 'Department Owner',
    multiSelect: true,
    data: departmentValueLabelPairs,
  }

  const designationFilters = {
    id: 'designation',
    title: 'Designation Access',
    multiSelect: true,
    data: designationValueLabelPairs,
  }

  const filterOptions = [
    fileTypeFilters,
    statusFilters,
    divisionOwnerFilters,
    departmentOwnerFilters,
    departmentFilters,
    designationFilters,
    syncedOnFilters,
  ].filter(Boolean)

  useEffect(() => {
    if (debouncedSearchQuery.length >= 3) {
      onSetFilters({
        ...currentFilters,
        ...filter,
        searchQuery: debouncedSearchQuery,
      })
    } else {
      onSetFilters({ ...currentFilters, ...filter, searchQuery: '' })
    }
  }, [debouncedSearchQuery])

  const [sortParams] = useState({
    id: 'title',
    desc: false,
  })

  const modifiedTableSettings = { ...tableSettings }
  const einsteinAdmin = get(
    useEinsteinAdministratorProfile(),
    'einsteinAdmin',
    {}
  )

  const columns = [
    ...tableConfigUtils.mapCellRenderToConfig(
      modifiedTableSettings,
      hospital,
      t
    ),
    {
      id: 'actions',
      Header: '',
      Cell: (props) =>
        ActionsCell({
          ...props,
          parentPage,
          einsteinAdmin,
          postHogModule,
          logPostHogEvent,
        }),
      disableSortBy: true,
      width: '18px',
    },
  ]

  const handleTableRowClick = (row) => {
    const disableActions = getDisabledActions(einsteinAdmin, { row })
    if (disableActions) return
    if (enableRowEdit) {
      row.toggleRowSelected()
    } else {
      onShowEditDocModal(row.original)
    }
  }

  const handleSortColumn = (column) => {
    if (typeof column.toggleSortBy === 'function') {
      column.toggleSortBy(!column.isSortedDesc)
    }

    onSort({
      id: column.id,
      desc: !column.isSortedDesc,
    })
  }

  const renderTable = () => {
    if (isLoading || isFetching) return <ProgressAnimation />

    return (
      <DataTable
        key={`${enableRowSelect}-${enableRowEdit}`}
        columns={columns}
        data={internalDocs}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onFetchNextPage={fetchNextPage}
        onFetchData={() => sortParams}
        onShowSubmission={{}}
        onDownloadSubmissions={{}}
        hideHeaderSelectionCheckBox
        showRowSelectedBar
        renderRowSelectInfo={(props) =>
          enableRowSelect && (
            <RowSelectComponent
              {...props}
              enableRowSelect={enableRowEdit}
              rowSelectionType={rowSelectionType}
              totalCount={(documentsData || {}).totalCount}
              filters={filter}
              setRowSelectionType={setRowSelectionType}
            />
          )
        }
        onShowEditDocModal={(document) => onShowEditDocModal(document)}
        onDeleteDocument={(document) => onDeleteDocument(document)}
        onAddToFavorites={(document) => onAddToFavorites(document)}
        onRowClick={handleTableRowClick}
        showDischargedLabel={false}
        show={false}
        enableRowSelect={enableRowSelect}
        disableCheckboxes={rowSelectionType === 'selectAll'}
        selectedFlatRows
        autoResetSelectedRows={false}
        t={t}
        history={{}}
        initialSortBy={{
          id: 'title',
          desc: false,
        }}
        getHeaderProps={(column) => ({
          onClick: () => handleSortColumn(column),
        })}
        logEventProps={{
          subSource:
            selectedTabId === 'internal_files'
              ? EventsConstant.DOCUMENTS_TABLE_SOURCE_INTERNAL
              : EventsConstant.DOCUMENTS_TAB_SELECTED_EXTERNAL,
          eventName: EventsConstant.DOCUMENTS_EDIT_SETTINGS,
          page: parentPage,
        }}
      />
    )
  }

  return (
    <Box>
      {/* Search bar */}
      <Flex m={2} justifyContent="space-between" alignItems="center" pr="16px">
        <Box display={['none', 'none', 'block']}>
          <Flex alignItems="center">
            <Box flex={[0.95, null]}>
              <InputField
                sanitize
                data-testid="documentsSearchInput"
                justifyContent="space-between"
                alignItems="center"
                placeholder="Search by title, tags or author"
                startIcon={
                  <FAIcon icon={faSearch} fontSize={16} color="fullShade" />
                }
                width={['100%', 500]}
                mb="8px"
                fontWeight="400"
                height="43.5px"
                endIcon={
                  !isEmpty(searchQuery) && (
                    <FAIcon
                      cursor="pointer"
                      onClick={() => {
                        setSearchQuery('')
                      }}
                      icon={faTimes}
                      fontSize={16}
                      color="fullShade"
                    />
                  )
                }
                lineHeigt="24px"
                fontSize={[14]}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
          </Flex>
        </Box>
        <Flex justifyContent="space-between" mb={3}>
          <SecondaryOutlinedButton
            color="fullShade"
            borderColor="mediumShade"
            borderRadius={8}
            alt="Reload"
            onClick={() => {
              refetch()
              logPostHogEvent(`${postHogModule}:refresh_button_click`)
            }}
            mr={2}
          >
            <SpinningIcon spin={isLoading || isFetching} />
          </SecondaryOutlinedButton>
          <DropdownMenu
            moveLeft="-10px"
            fontWeight="400"
            label={
              <SecondaryOutlinedButton
                mr={2}
                display={internalDocs.length === 0 ? 'none' : 'flex'}
                endIcon={
                  <FAIcon icon={faCog} fontSize={14} color="darkestShade" />
                }
                color="darkestShade"
                borderRadius="8px"
                logEventProps={{
                  subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE,
                  eventName: EventsConstant.DOCUMENTS_CUSTOM_SELECTION,
                  page: parentPage,
                }}
              >
                Actions
              </SecondaryOutlinedButton>
            }
            actions={actions}
            width="200px"
          />
          <Filter
            testId="documentsFilterButton"
            activeFilters={activeFilters}
            minWidth={550}
            filterOptions={filterOptions}
            onFilterChange={onToggleFilter}
            open={showFilters}
            onOpen={onToggleFilters}
            onResetFilters={onResetFilters}
            onSave={onApplyFilters}
            onCancel={onFilterCancel}
            logEventPropsApply={{
              subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE,
              eventName: EventsConstant.DOCUMENTS_FILTERS_APPLIED,
              page: parentPage,
            }}
            logEventPropsCancel={{
              subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE,
              eventName: EventsConstant.DOCUMENTS_FILTERS_CENCELED,
              page: parentPage,
            }}
            logEventPropsOpened={{
              subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE,
              eventName: EventsConstant.DOCUMENTS_FILTERS_OPENED,
              page: parentPage,
            }}
            logEventPropsReset={{
              subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE,
              eventName: EventsConstant.DOCUMENTS_FILTERS_RESET,
              page: parentPage,
            }}
            postHogModule={postHogModule}
          />
        </Flex>
      </Flex>

      {/* Table */}
      <Box ml={16} mr={20} data-testid="documentsTable">
        {renderTable()}
      </Box>
    </Box>
  )
}

export const TableCell = ({ children, cursor }) => (
  <Box cursor={cursor || 'pointer'} onClick={() => {}}>
    {children}
  </Box>
)

export const DocumentsActionMenu = ({
  onShowEditDocModal,
  onAddToFavorites,
  label,
  parentPage,
  disableActions,
  postHogModule,
  logPostHogEvent,
}) => (
  <div data-testid="elipsis">
    <ActionMenu label={label}>
      <div data-testid="editDocumentOption">
        <ActionMenuItem
          onClick={(e) => {
            if (!disableActions) {
              e.stopPropagation()
              onShowEditDocModal({ title: 'Faq' })
            }
          }}
          logEventProps={{
            subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE_INTERNAL,
            eventName: EventsConstant.DOCUMENTS_EDIT_SETTINGS,
            page: parentPage,
          }}
        >
          <ActionMenuText opacity={disableActions ? 0.6 : 1}>
            Edit Document settings
          </ActionMenuText>
        </ActionMenuItem>
      </div>
      <div data-testid="addToFavouritesOption">
        <ActionMenuItem
          onClick={(e) => {
            e.stopPropagation()
            onAddToFavorites()
            logPostHogEvent(`${postHogModule}:file_item_add_favourites_click`)
          }}
          logEventProps={{
            subSource: EventsConstant.DOCUMENTS_TABLE_SOURCE_INTERNAL,
            eventName: EventsConstant.DOCUMENTS_ADD_TO_FAVOURITES,
            page: parentPage,
          }}
        >
          <ActionMenuText>Add to Favourites</ActionMenuText>
        </ActionMenuItem>
      </div>
    </ActionMenu>
  </div>
)

export default DocumentsTable
